import Routes from "@navigations/Routes";
import { CommonActions } from "@react-navigation/native";
import { ProductCategory } from "@utils/constants";
import { formatNumberWithPrecision } from "@utils/product/productFormatters";
import { isMeleeItem } from "@utils/product/productValidators";
import { getSectionName } from "@utils/product/searchResultsNavigation";

type NavigationState = {
  screen: string;
  params?: any;
};

export const getSearchIndexByProductType = (
  history: NavigationState[],
  targetProductID: number
): number | null => {
  for (let i = 0; i < history.length; i++) {
    const entry = history[i];
    if (
      (entry.screen === Routes.FilterSplitScreen ||
        entry.screen === Routes.SearchResultScreen) &&
      entry.params?.productType === targetProductID
    ) {
      return i;
    }
  }
  return null;
};

export const getSearchIndexOfMatchingStone = (
  history: NavigationState[]
): number | null => {
  for (let i = 0; i < history.length; i++) {
    const entry = history[i];
    if (entry.screen === Routes.FindMatchScreen) {
      return i;
    }
  }
  return null;
};

export const additionalButton = {
  see_buy_request: {
    icon: {
      data: "",
      type: "font",
    },
    name: "see_buy_request",
    type: 0,
    label: "productDetailPage.seeBuyRequest.button.text",
    order: 5,
    category: "diam",
    description: "bottom_right",
    price_modes: [2, 1],
    segment_identifier: "see_buy_request",
  },
  see_order_request: {
    icon: {
      data: "",
      type: "font",
    },
    name: "see_order_cart",
    type: 0,
    label: "productDetailPage.seeOrderCart.button.text",
    order: 5,
    category: "diam",
    description: "bottom_right",
    price_modes: [2, 1],
    segment_identifier: "see_order_cart",
  },
  total_price: {
    icon: {
      data: "",
      type: "font",
    },
    name: "total_price",
    type: 0,
    label: "productDetailPage.totalPrice.button.text",
    order: 0,
    category: "diam",
    description: "bottom_right",
    price_modes: [2, 1],
    segment_identifier: "total_price",
  },
};

export const getReviewPageButtons = ({
  fromRequest,
  fromOrderCart,
  pdpLayout,
  product_type,
  theme,
  priceMode,
  showBulkRequest,
  showOrderCartButton,
  itemDetail,
  stoneItemDetail,
  matchingPairItemDetail,
  setReviewTotalPrice,
  userInfo,
}: {
  fromRequest: boolean;
  fromOrderCart: boolean;
  pdpLayout: any;
  product_type: string;
  theme: any;
  priceMode: any;
  showBulkRequest: boolean;
  showOrderCartButton: boolean;
  itemDetail: any;
  stoneItemDetail: any;
  matchingPairItemDetail: any;
  setReviewTotalPrice: (val: number) => void;
  userInfo: any;
}) => {
  let customButtons: any[] = [];
  const platform = !theme.isMobile ? "web" : "mobile";
  const buttonsConfig =
    pdpLayout[product_type]?.review_page?.bottom_buttons?.[platform] || {};

  if (fromRequest || fromOrderCart) {
    Object.values(buttonsConfig).forEach((btn: any) => {
      if (btn.name === "ask_supplier") {
        customButtons.push(btn);
      }
    });
    if (
      !theme.isMobile &&
      itemDetail?.custom_design_configuration &&
      !buttonsConfig?.total_price &&
      (fromRequest || fromOrderCart)
    ) {
      customButtons.push(additionalButton.total_price);
    }
    if (fromRequest) {
      customButtons.push(additionalButton.see_buy_request);
    }
    if (fromOrderCart) {
      customButtons.push(additionalButton.see_order_request);
    }
    if (stoneItemDetail && itemDetail) {
      let totalPrice =
        Number(stoneItemDetail.total_sales_price) +
        Number(itemDetail.total_sales_price);
      if (
        matchingPairItemDetail &&
        Object.keys(matchingPairItemDetail).length > 0
      ) {
        totalPrice += Number(matchingPairItemDetail.total_sales_price);
      }
      setReviewTotalPrice(
        Number(
          formatNumberWithPrecision(totalPrice, {
            precision:
              userInfo?.roundUpPrices() && !isMeleeItem(stoneItemDetail)
                ? 0
                : 2,
          })
        )
      );
    }
    return Object.values(
      customButtons.reduce((acc: any, button: any) => {
        if (!acc[button.description]) {
          acc[button.description] = [];
        }
        acc[button.description].push(button);
        return acc;
      }, {})
    );
  }
  let buttons = { ...buttonsConfig };
  if (
    !theme.isMobile &&
    itemDetail?.custom_design_configuration &&
    !buttons?.total_price
  ) {
    buttons.total_price = {
      icon: {
        data: "",
        type: "font",
      },
      name: "total_price",
      type: 0,
      label: "productDetailPage.totalPrice.button.text",
      order: 0,
      category: "diam",
      description: "bottom_right",
      price_modes: [2, 1],
      segment_identifier: "total_price",
    };
  }
  let buttonsArray = Object.keys(buttons)
    .filter(
      (button) =>
        buttons[button].price_modes.length > 0 &&
        buttons[button].price_modes.includes(priceMode) &&
        (buttons[button]?.name === "add_to_bulk" ? showBulkRequest : true) &&
        (buttons[button]?.name === "add_to_cart" ? showOrderCartButton : true)
    )
    .sort((a, b) => {
      const descPriority: Record<string, number> = {
        bottom_left: 0,
        bottom_right: 1,
      };
      const descA = buttons[a].description as string;
      const descB = buttons[b].description as string;
      if (descA !== descB) {
        return (descPriority[descA] ?? 99) - (descPriority[descB] ?? 99);
      }
      return buttons[a].order - buttons[b].order;
    })
    .map((button) => {
      if (
        buttons[button]?.name === "add_to_cart" &&
        itemDetail?.in_cart &&
        !userInfo.enableThirdPartyAddToCart()
      ) {
        return {
          ...buttons[button],
          name: "added_to_cart",
        };
      }
      return {
        ...buttons[button],
        label: buttons[button].label,
      };
    });
  buttonsArray = Object.values(
    buttonsArray.reduce((acc: any, button: any) => {
      if (!acc[button.description]) {
        acc[button.description] = [];
      }
      acc[button.description].push(button);
      return acc;
    }, {})
  );
  if (stoneItemDetail && itemDetail) {
    let totalPrice =
      Number(stoneItemDetail.total_sales_price) +
      Number(itemDetail.total_sales_price);
    if (
      matchingPairItemDetail &&
      Object.keys(matchingPairItemDetail).length > 0
    ) {
      totalPrice += Number(matchingPairItemDetail.total_sales_price);
    }
    setReviewTotalPrice(
      Number(
        formatNumberWithPrecision(totalPrice, {
          precision:
            userInfo?.roundUpPrices() && !isMeleeItem(stoneItemDetail) ? 0 : 2,
        }) ?? "0"
      )
    );
  }
  return buttonsArray;
};

const parseSavedSearch = (savedSearch: any) => {
  if (!savedSearch) return null;
  if (typeof savedSearch !== "string") return savedSearch;

  try {
    return JSON.parse(savedSearch);
  } catch (error) {
    console.error("Error parsing saved search:", error);
    return null;
  }
};

// Changing the stone clears `jewelry_styles` from the live filters but keeps the
// style values it points at, so the Jewelry Type selection has to be read back
// from the entry's own snapshot to stay whole.
export const mergeSavedSearchWithLiveFilters = (
  savedSearch: any,
  liveFilters: any
) => {
  if (liveFilters?.jewelry_styles) return liveFilters;

  const snapshot = parseSavedSearch(savedSearch);
  if (!snapshot?.jewelry_styles) return liveFilters;

  return { ...liveFilters, jewelry_styles: snapshot.jewelry_styles };
};

export const isSettingSearchRoute = (screen?: string, params?: any) =>
  (screen === Routes.SearchResultScreen ||
    screen === Routes.FilterSplitScreen) &&
  (params?.productType === ProductCategory.JEWELRY ||
    params?.productType === ProductCategory.LABGROWN_JEWELRY);

export const refreshSavedSearchForEntry = <
  T extends { screen?: string; name?: string; params?: any }
>(
  item: T,
  auxPayload: any
): T => {
  const screen = item.screen || item.name;
  if (!isSettingSearchRoute(screen, item.params)) return item;
  if (!item.params?.saved_search) return item;

  const section =
    item.params?.sectionName || getSectionName(item.params?.productType, "");
  const liveFilters = auxPayload?.[section];

  if (!liveFilters || Object.keys(liveFilters).length === 0) return item;

  return {
    ...item,
    params: {
      ...item.params,
      saved_search: JSON.stringify(
        mergeSavedSearchWithLiveFilters(item.params?.saved_search, liveFilters)
      ),
    },
  };
};

export const popToScreen = (
  navigation: any,
  screens: any,
  activeScreen: any,
  productType: any,
  theme: any,
  isModifiedScreen: any,
  fromReviewCard = false
) => {
  const mRoutes = screens.map((scr) => ({
    name: scr.screen,
    params: scr.params,
  }));

  let mIndex = mRoutes.findIndex(
    (s) => s.name === activeScreen && s.params?.productType === productType
  );

  if (mIndex === -1) {
    mIndex = 0;
  }

  const popCount = mRoutes.length - (mIndex + 1);
  const updatedParams = mRoutes[mIndex]?.params;

  if (popCount === 3 && isModifiedScreen && fromReviewCard) {
    navigation.reset({
      index: 0,
      routes: [
        theme.isLargeScreen
          ? { name: screens[1]?.screen, params: screens[1]?.params }
          : { name: Routes.Explore },
      ],
    });
  } else if (
    updatedParams &&
    (updatedParams?.productType === ProductCategory.JEWELRY ||
      updatedParams?.productType === ProductCategory.LABGROWN_JEWELRY)
  ) {
    // After a browser reload the review page is restored without its underlying
    // stack, so the real navigation stack no longer mirrors the logical
    // breadcrumb history. Slicing it by the history-derived popCount would yield
    // an invalid state and bounce the user to explore. If the target screen is
    // not present in the real stack, navigate to it directly using the
    // persisted history params instead.
    const navState = navigation.getState();
    const actualRoutes = navState?.routes ?? [];
    const actualCurrentIndex = navState?.index ?? actualRoutes.length - 1;
    let targetActualIndex = -1;
    for (let i = actualCurrentIndex; i >= 0; i--) {
      if (
        actualRoutes[i].name === activeScreen &&
        String(actualRoutes[i].params?.productType) === String(productType)
      ) {
        targetActualIndex = i;
        break;
      }
    }

    if (targetActualIndex === -1) {
      navigation.navigate(activeScreen, updatedParams);
      return;
    }

    navigation.dispatch((state) => {
      const newRoutes = state.routes.slice(0, state.routes.length - popCount);

      const updatedRoutes = newRoutes.map((route, index) => {
        if (index === newRoutes.length - 1) {
          return {
            ...route,
            params: {
              ...route.params,
              ...updatedParams,
            },
          };
        }
        return route;
      });

      return CommonActions.reset({
        ...state,
        routes: updatedRoutes,
        index: updatedRoutes.length - 1,
      });
    });
  } else {
    const navState = navigation.getState();
    const actualRoutes = navState?.routes ?? [];
    const actualCurrentIndex = navState?.index ?? actualRoutes.length - 1;

    let targetActualIndex = -1;
    for (let i = actualCurrentIndex; i >= 0; i--) {
      if (
        actualRoutes[i].name === activeScreen &&
        String(actualRoutes[i].params?.productType) === String(productType)
      ) {
        targetActualIndex = i;
        break;
      }
    }

    if (targetActualIndex !== -1) {
      navigation.pop(actualCurrentIndex - targetActualIndex);
    } else {
      const targetParams = mRoutes[mIndex]?.params;
      navigation.navigate(activeScreen, targetParams);
    }
  }
};

export const remapConfigKeys = (displayConfig, configDetails) => {
  if (displayConfig && configDetails) {
    const optionNameToType = {};
    displayConfig.forEach((option) => {
      let mKey = option.option_name.toLowerCase();
      if (
        option.option_name === "Supported Shapes" ||
        option.option_name === "Supported Shape"
      ) {
        mKey = "supported shapes";
      }
      optionNameToType[mKey] = option.option_type;
    });

    const updatedConfig = {};
    Object.keys(configDetails).forEach((key) => {
      const keyLower = key.toLowerCase();
      const updatedKey = optionNameToType[keyLower] || key;
      updatedConfig[updatedKey] = configDetails[key];
    });
    return updatedConfig;
  } else {
    return {};
  }
};
